import { Box, IconButton, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useOutsideClick } from "../../../../hooks/useOutSideClick";
import { BellIcon } from "../../../images";
import { Activities } from "../../../../types/apiTypes";
import { NotificationSection } from "./NotificationSection";
import {
  useAppDispatch,
  useAppSelector,
  useFormattedActivities,
} from "../../../../hooks";
import { setIsNotificationsOpen } from "../../../../features/companySlice";
import { NotificationTypes } from "../../../../types/enums";

const notificationList = [
  "Analyzing Accounts",
  "Searching for New Contacts",
  "Generating Account Plans",
  "Generating SDR Playbooks",
];

export const NotificationBtn = ({ campaign_id }: { campaign_id: string }) => {
  const dispatch = useAppDispatch();
  const isNotificationsOpen = useAppSelector(
    (state) => state.company.isNotificationsOpen,
  );
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [showAnalyzeList, setShowAnalyzeList] = useState<Activities[]>([]);
  const [showScrapingList, setShowScrapingList] = useState<Activities[]>([]);
  const [showPlanList, setShowPlanList] = useState<Activities[]>([]);
  const [showPlaybookList, setShowPlaybookList] = useState<Activities[]>([]);
  const [openAccordion, setOpenAccordion] = useState<string>("");

  const dropdownWrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { scraping, analysis, accountPlan, playbook } =
    useFormattedActivities(campaign_id);

  useEffect(() => {
    if (!isOpen) {
      setShowAnalyzeList([]);
      setShowScrapingList([]);
      setShowPlanList([]);
      setShowPlaybookList([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isNotificationsOpen) {
      setOpenAccordion("");
      setIsOpen(true);
      dispatch(setIsNotificationsOpen(false));
    }
  }, [isNotificationsOpen]);

  useEffect(() => {
    setOpenAccordion("");
    setTimeout(() => setOpenAccordion(openAccordion), 0);
  }, [showScrapingList, showAnalyzeList]);

  const closeHandler = () => {
    setIsOpen(false);
  };

  useOutsideClick({
    elementRef: dropdownRef,
    onOutsideClick: closeHandler,
    isOpened: isOpen,
    wrapperRef: dropdownWrapperRef,
  });

  const isNoData =
    analysis.length === 0 &&
    scraping.length === 0 &&
    accountPlan.length === 0 &&
    playbook.length === 0;

  return (
    <Stack
      ref={dropdownWrapperRef}
      position="relative"
      gap="16px"
      alignItems="center"
      justifyContent="center"
    >
      <IconButton
        onClick={() => setIsOpen(!isOpen)}
        size="small"
        sx={{
          ml: "auto",
          color: "#828282",
        }}
      >
        <BellIcon />
      </IconButton>
      {!isNoData && (
        <Box
          sx={{
            position: "absolute",
            top: 10,
            right: 6,
            width: "10px",
            height: "10px",
            border: "2px solid #F7F7F7",
            backgroundColor: "#FD8430",
            borderRadius: "50%",
          }}
        />
      )}

      {isOpen && (
        <Stack
          ref={dropdownRef}
          sx={{
            backgroundColor: "white",
            position: "absolute",
            zIndex: 101,
            right: -56,
            top: 68,
            boxShadow: "0px 4px 15px 0px #00000040",
            borderRadius: "6px",
            padding: "8px 6px",
            width: "470px",
            gap: "8px",
            maxHeight: "calc(100vh - 200px)",
          }}
        >
          {isNoData ? (
            <Stack
              height="calc(100vh - 200px)"
              maxHeight="522px"
              alignItems="center"
              justifyContent="center"
            >
              <Typography variant="body1" color="#4C545B" textAlign="center">
                A list of companies will appear here once you
              </Typography>
              <Typography variant="body1" color="#4C545B" textAlign="center">
                start analysis or contacts search
              </Typography>
            </Stack>
          ) : (
            <>
              <NotificationSection
                campaign_id={campaign_id}
                title={notificationList[0]}
                setShowList={setShowAnalyzeList}
                setOpenAccordion={setOpenAccordion}
                initialExpanded={openAccordion === notificationList[0]}
                showList={showAnalyzeList}
                activities={analysis}
                type={NotificationTypes.analysis}
              />
              <NotificationSection
                campaign_id={campaign_id}
                title={notificationList[1]}
                setShowList={setShowScrapingList}
                setOpenAccordion={setOpenAccordion}
                initialExpanded={openAccordion === notificationList[1]}
                showList={showScrapingList}
                activities={scraping}
                type={NotificationTypes.scraping}
              />
              <NotificationSection
                campaign_id={campaign_id}
                title={notificationList[2]}
                setShowList={setShowPlanList}
                setOpenAccordion={setOpenAccordion}
                initialExpanded={openAccordion === notificationList[2]}
                showList={showPlanList}
                activities={accountPlan}
                type={NotificationTypes.account_plan_generation}
              />
              <NotificationSection
                campaign_id={campaign_id}
                title={notificationList[3]}
                setShowList={setShowPlaybookList}
                setOpenAccordion={setOpenAccordion}
                initialExpanded={openAccordion === notificationList[3]}
                showList={showPlaybookList}
                activities={playbook}
                type={NotificationTypes.sdr_playbook_generation}
              />
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
};
